import React from 'react';
import { formatNumber } from 'utils/formatter';
import { commaFormat } from 'components/features/new-report/utils/formatter';
import { calculateRemainTargetDate } from 'components/features/target-achievement-support/utils/birthday';
import { calculatePercentage } from 'components/features/target-achievement-support/utils/targetInfo';
import { Target } from 'components/features/target-achievement-support/utils/type';
import {
  ContextMenu,
  ContextMenuContainer,
  ContextMenuRow,
  getTargetContextMenuRows,
} from './ContextMenu';
import { TargetItemIcon } from './TargetItemIcon';
import { Percentage } from '../share/Percentage';

type Props = {
  target: Target;
  birthday: Date;
  isOpenContextMenu: boolean;
  goEditTitle: () => void;
  goEditTargetAmount: () => void;
  goTargetDetail: () => void;
  endTarget: () => void;
  openContextMenu: () => void;
  hideContextMenu: () => void;
};

export const TargetItem = ({
  target: { assetAmount, ...target },
  goEditTargetAmount,
  goEditTitle,
  endTarget,
  goTargetDetail,
  isOpenContextMenu,
  birthday,
  hideContextMenu,
  openContextMenu,
}: Props): React.ReactElement => {
  const toggleContextMenu = isOpenContextMenu
    ? hideContextMenu
    : openContextMenu;

  const rows = getTargetContextMenuRows({
    editTargetAmount: goEditTargetAmount,
    editTitle: goEditTitle,
    endTarget,
  });

  const isAchieved =
    assetAmount >= target.targetAmount || target.targetAmount === 0;

  const result = calculatePercentage(target.targetAmount, assetAmount);
  const percentage = Number.isNaN(result) ? 0 : result;

  const startDate = new Date(target.startDate);
  const endDate = new Date(target.endDate);

  const year = startDate.getFullYear();
  const month = startDate.getMonth() + 1;
  const day = startDate.getDate();

  const Title = ({ className }: { className?: string }): React.ReactElement => (
    <div className={`font-bold text-lg ${className ?? ''}`}>{target.title}</div>
  );

  const RemainDate = ({
    className,
  }: {
    className?: string;
  }): React.ReactElement => {
    const remainTargetDate = calculateRemainTargetDate({
      birthday,
      targetDate: endDate,
    });

    return (
      <div
        className={`flex flex-row items-center text-bluegray-500 text-xs ${className ??
          ''}`}>
        <div className="mr-1">目標まであと</div>
        <div>
          {remainTargetDate.year ? `${remainTargetDate.year}年` : ''}
          {remainTargetDate.month ? `${remainTargetDate.month}ヶ月` : ''}
        </div>
      </div>
    );
  };

  return (
    <>
      <div
        className="p-4 bg-white rounded-lg border border-bluegray-200 shadow cursor-pointer"
        onClick={goTargetDetail}>
        <div className="flex flex-row">
          <div className="flex flex-row gap-2 items-center mr-auto mb-6">
            <div style={{ width: '60px', height: '60px' }}>
              <TargetItemIcon {...{ targetType: target.refType }} />
            </div>
            <div className="flex flex-col">
              <Title />
              <RemainDate />
              <div className="items-center text-bluegray-500 text-xs">
                登録日：{year + '/' + month + '/' + day}
              </div>
            </div>
          </div>
          <div className="flex flex-col mt-1">
            <ContextMenu
              {...{
                isOpen: isOpenContextMenu,
                onClick: toggleContextMenu,
                onBlur: hideContextMenu,
              }}>
              <ContextMenuContainer>
                {rows.map((p, i) => (
                  <ContextMenuRow {...p} key={i} />
                ))}
              </ContextMenuContainer>
            </ContextMenu>
          </div>
        </div>
        <div>
          <div>
            <Percentage
              {...{
                percentage,
                backgroundColorTailwind: 'bg-gray-300',
                foregroundColorTailwind: isAchieved ? null : 'bg-blue-500',
                isShowPercentageStr: true,
                isAchieved,
              }}
            />
          </div>
          <div className="flex flex-row items-center mt-1">
            <div className="text-bluegray-500 text-xs">現在の資産</div>
            <div className="text-bluegray-700 flex-row flex items-center ml-auto">
              <div className="text-sm">{commaFormat(assetAmount)}</div>
              <div className="text-xs">円</div>
              <div className="mr-0.5 text-xs">／目標</div>
              <div className="text-sm">
                {formatNumber(target.targetAmount / 10000, {})}
              </div>
              <div className="text-xs">万円</div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
